import { giphyBlack, giphyDarkestGrey, giphyDarkGrey } from '@giphy/js-brand'
import { uniqueId } from 'lodash'
import { ReactNode, useEffect, useRef } from 'react'
import { ButtonProps } from '../button'
import { IconButtonProps } from '../icon-button'
import { PortalTooltip, PortalTooltipContext, useTooltip } from '../tooltip'
import { TooltipProps } from '../tooltip/hooks/use-tooltip'
import { ArrowButton, Button, Container } from './style'

export interface DropdownButtonProps extends Omit<ButtonProps, 'icon' | 'iconLocation'> {
    iconButtonOptions?: Partial<IconButtonProps>
    label: ReactNode
    tooltipOptions?: Partial<TooltipProps>
    onToggle?: (isOpen: boolean) => void
}

const DropdownButtonComponent = ({
    iconButtonOptions = {},
    children,
    className,
    color = giphyDarkGrey,
    label,
    size = 'small',
    style,
    tooltipOptions = {},
    onToggle,
    ...rest
}: DropdownButtonProps) => {
    const idRef = useRef<string>(uniqueId('dropdown-button'))
    const defaultIsOpenRef = useRef<boolean | null>(false)
    const { isOpen, listeners, setNodeRef } = useTooltip({
        color: giphyDarkestGrey,
        direction: 'down',
        id: idRef.current,
        ...tooltipOptions,
    })

    useEffect(() => {
        if (isOpen !== defaultIsOpenRef.current) {
            defaultIsOpenRef.current = null
            onToggle?.(isOpen)
        }
    }, [isOpen])

    return (
        <>
            <Container className={className} controlColor={color} controlSize={size} ref={setNodeRef} style={style}>
                <Button {...listeners} {...rest} color={color} size={size}>
                    {label}
                </Button>
                <ArrowButton
                    color={giphyBlack}
                    iconScale={0.8}
                    {...iconButtonOptions}
                    {...listeners}
                    icon="dropdown"
                    size={size}
                />
            </Container>
            <PortalTooltip isOpen={isOpen}>{children}</PortalTooltip>
        </>
    )
}

const DropdownButton = (props: DropdownButtonProps) => (
    <PortalTooltipContext>
        <DropdownButtonComponent {...props} />
    </PortalTooltipContext>
)

export default DropdownButton
